import { memo } from "react";
import { useTranslation } from "react-i18next";
import { TrendingUp, Sparkles, Zap, Lock, X } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Right-side deck info + current-player quick stats panel.
 * Mirrors EventLogPanel's drawer pattern: inline ≥md, fullscreen
 * overlay on mobile when `show` is true (toggled from GameHeader).
 * `memo`-wrapped (KNOWN_ISSUES §1.36).
 */
function DeckPanel({ game, currentPlayer, show, onClose }) {
  const { t } = useTranslation();
  return (
    <aside
      className={`${show ? "fixed inset-0 z-40 bg-black/30 flex justify-end" : "hidden"} md:relative md:flex md:bg-transparent`}
    >
      <div
        className={`${show ? "w-64 h-full" : "w-40 lg:w-48"} border-l border-[#1A237E]/10 dark:border-[#E3F2FD]/10 bg-white/95 dark:bg-slate-900/95 md:bg-white/40 dark:md:bg-slate-900/40 p-2 lg:p-3 space-y-3 lg:space-y-4 overflow-y-auto`}
        data-testid="deck-panel"
      >
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-[#1A237E] dark:text-[#E3F2FD]" style={{ fontFamily: "Fredoka" }}>
            {t("deckPanel.title")}
          </h3>
          <Button variant="ghost" size="sm" className="md:hidden p-1" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>
      <div className="deck-pile bg-gradient-to-br from-pink-400 to-pink-600 rounded-xl p-3 text-white text-center">
        <TrendingUp className="w-6 h-6 mx-auto mb-1" />
        <p className="text-xs font-bold">{t("deckPanel.crypto")}</p>
        <p className="text-2xl font-bold">{game.crypto_deck_count}</p>
        <p className="text-[10px] opacity-70">{t("deckPanel.discard", { count: game.crypto_discard_count })}</p>
      </div>
      <div className="deck-pile bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl p-3 text-white text-center">
        <Sparkles className="w-6 h-6 mx-auto mb-1" />
        <p className="text-xs font-bold">{t("deckPanel.effect")}</p>
        <p className="text-2xl font-bold">{game.effect_deck_count}</p>
        <p className="text-[10px] opacity-70">{t("deckPanel.discard", { count: game.effect_discard_count })}</p>
      </div>
      <div
        className={`rounded-xl p-3 text-center border-2 ${
          game.market_melee_count >= 2 ? "border-red-400 bg-red-50" : "border-[#1A237E]/10 dark:border-[#E3F2FD]/10 bg-white/50 dark:bg-slate-900/50"
        }`}
      >
        <Zap className="w-6 h-6 mx-auto mb-1 text-[#FF5A5F]" />
        <p className="text-xs font-bold text-[#1A237E] dark:text-[#E3F2FD]">{t("deckPanel.marketMelee")}</p>
        <p className="text-2xl font-bold text-[#FF5A5F]">{game.market_melee_count}/3</p>
      </div>

      <div className="border-t border-[#1A237E]/10 dark:border-[#E3F2FD]/10 pt-3">
        <p className="text-xs font-bold text-[#1A237E] dark:text-[#E3F2FD] mb-1">{t("deckPanel.stats.title")}</p>
        <div className="text-xs text-[#1A237E]/70 dark:text-[#E3F2FD]/70 space-y-1">
          <p>
            {t("deckPanel.stats.cryptoValue")} <strong className="text-[#38B6FF]">${currentPlayer.crypto_value}</strong>
          </p>
          <p>
            {t("deckPanel.stats.stablecoins")} <strong className="text-green-600">${currentPlayer.stablecoin_available}</strong>
          </p>
          {currentPlayer.stablecoin_locked > 0 && (
            <p className="flex items-center gap-1">
              <Lock className="w-3 h-3 text-[#FF5A5F]" />
              {t("deckPanel.stats.locked")} <strong className="text-[#FF5A5F]">${currentPlayer.stablecoin_locked}</strong>
            </p>
          )}
        </div>
      </div>
      </div>
    </aside>
  );
}

export default memo(DeckPanel);
